import { useState } from 'react';
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Paper,
  Snackbar,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { useForm } from 'react-hook-form';
import { useCreateOrganization, useUpdateOrganization, useCvrLookup, useCreateInvite } from '../../hooks/useApi';
import type { Organization, DoraApiEnvironment, OrganizationLanguage, InviteCredentials } from '../../types';

interface OrganizationFormProps {
  organization: Organization | null;
  onClose: () => void;
}

interface FormData {
  name: string;
  cvr: string;
  address: string;
  city: string;
  postalCode: string;
  doraApiKey: string;
  doraApiEnvironment: DoraApiEnvironment;
  language: OrganizationLanguage;
  fmsApiKey: string;
  fmsCustomerId: string;
}

interface InviteFormData {
  email: string;
  name: string;
}

export default function OrganizationForm({ organization, onClose }: OrganizationFormProps) {
  const [showApiKey, setShowApiKey] = useState(false);
  const [cvrError, setCvrError] = useState<string | null>(null);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteCredentials, setInviteCredentials] = useState<InviteCredentials | null>(null);
  const [copySnackbar, setCopySnackbar] = useState(false);
  const [createdOrgId, setCreatedOrgId] = useState<string | null>(null);

  const createOrg = useCreateOrganization();
  const updateOrg = useUpdateOrganization();
  const cvrLookup = useCvrLookup();
  const createInvite = useCreateInvite();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    defaultValues: {
      name: organization?.name || '',
      cvr: organization?.cvr || '',
      address: organization?.address || '',
      city: organization?.city || '',
      postalCode: organization?.postalCode || '',
      doraApiKey: '',
      doraApiEnvironment: organization?.doraApiEnvironment || 'PRODUCTION',
      language: organization?.language || 'da',
      fmsApiKey: organization?.fmsApiKey || '',
      fmsCustomerId: organization?.fmsCustomerId || '',
    },
  });

  const {
    register: registerInvite,
    handleSubmit: handleSubmitInvite,
    formState: { errors: inviteErrors },
    reset: resetInviteForm,
  } = useForm<InviteFormData>({
    defaultValues: {
      email: '',
      name: '',
    },
  });

  const cvrValue = watch('cvr');

  const handleCvrLookup = async () => {
    if (!cvrValue || cvrValue.length < 1) {
      setCvrError('CVR/Org.nr. er påkrævet');
      return;
    }

    setCvrError(null);
    try {
      const result = await cvrLookup.mutateAsync(cvrValue);
      if (result) {
        setValue('name', result.name);
        if (result.address) setValue('address', result.address);
        if (result.city) setValue('city', result.city);
        if (result.postalCode) setValue('postalCode', result.postalCode);
      }
    } catch {
      setCvrError('Kunne ikke finde virksomhed - CVR opslag virker kun for danske virksomheder');
    }
  };

  const onSubmit = async (data: FormData) => {
    try {
      if (organization) {
        await updateOrg.mutateAsync({
          id: organization.id,
          data: {
            name: data.name,
            cvr: data.cvr,
            address: data.address || undefined,
            city: data.city || undefined,
            postalCode: data.postalCode || undefined,
            doraApiKey: data.doraApiKey || undefined,
            doraApiEnvironment: data.doraApiEnvironment,
            language: data.language,
            fmsApiKey: data.fmsApiKey,
            fmsCustomerId: data.fmsCustomerId,
          },
        });
        onClose();
      } else {
        const newOrg = await createOrg.mutateAsync({
          name: data.name,
          cvr: data.cvr,
          address: data.address || undefined,
          city: data.city || undefined,
          postalCode: data.postalCode || undefined,
          doraApiKey: data.doraApiKey,
          doraApiEnvironment: data.doraApiEnvironment,
          language: data.language,
          fmsApiKey: data.fmsApiKey || undefined,
          fmsCustomerId: data.fmsCustomerId || undefined,
        });
        // After creating, show the invite form
        setCreatedOrgId(newOrg.id);
        setShowInviteForm(true);
      }
    } catch {
      // Error handled by mutation
    }
  };

  const onSubmitInvite = async (data: InviteFormData) => {
    if (!createdOrgId) return;

    try {
      const result = await createInvite.mutateAsync({
        organizationId: createdOrgId,
        data: {
          email: data.email,
          name: data.name,
        },
      });
      setInviteCredentials(result);
      resetInviteForm();
    } catch {
      // Error handled by mutation
    }
  };

  const copyToClipboard = async (text: string) => {
    await navigator.clipboard.writeText(text);
    setCopySnackbar(true);
  };

  const getLoginUrl = () => {
    return `${window.location.origin}/login`;
  };

  const getFullInviteText = () => {
    if (!inviteCredentials) return '';
    return `Login til DORA Insight

URL: ${getLoginUrl()}
Email: ${inviteCredentials.credentials.email}
Adgangskode: ${inviteCredentials.credentials.password}

Organisation: ${inviteCredentials.organization.name}`;
  };

  const isEditing = !!organization;

  // Show invite form after creating organization
  if (showInviteForm) {
    return (
      <>
        <DialogTitle>
          Opret bruger til organisationen
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {inviteCredentials ? (
              <Box>
                <Alert severity="success" sx={{ mb: 2 }}>
                  Bruger oprettet! Kopiér loginoplysningerne herunder.
                </Alert>

                <Paper sx={{ p: 2, bgcolor: 'grey.50', mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Login URL
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', flex: 1, wordBreak: 'break-all' }}>
                      {getLoginUrl()}
                    </Typography>
                    <IconButton size="small" onClick={() => copyToClipboard(getLoginUrl())}>
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Box>

                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Email
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', flex: 1 }}>
                      {inviteCredentials.credentials.email}
                    </Typography>
                    <IconButton size="small" onClick={() => copyToClipboard(inviteCredentials.credentials.email)}>
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Box>

                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Adgangskode
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', flex: 1 }}>
                      {inviteCredentials.credentials.password}
                    </Typography>
                    <IconButton size="small" onClick={() => copyToClipboard(inviteCredentials.credentials.password)}>
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Paper>

                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<ContentCopyIcon />}
                  onClick={() => copyToClipboard(getFullInviteText())}
                  sx={{ mb: 2 }}
                >
                  Kopiér alle oplysninger
                </Button>

                <Button
                  variant="text"
                  fullWidth
                  startIcon={<PersonAddIcon />}
                  onClick={() => {
                    setInviteCredentials(null);
                    resetInviteForm();
                  }}
                >
                  Opret endnu en bruger
                </Button>
              </Box>
            ) : (
              <form onSubmit={handleSubmitInvite(onSubmitInvite)}>
                {createInvite.error && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {(createInvite.error as Error)?.message || 'Der opstod en fejl'}
                  </Alert>
                )}

                <TextField
                  label="Navn"
                  fullWidth
                  {...registerInvite('name', { required: 'Navn er påkrævet' })}
                  error={!!inviteErrors.name}
                  helperText={inviteErrors.name?.message}
                  sx={{ mb: 2 }}
                />

                <TextField
                  label="Email"
                  fullWidth
                  type="email"
                  {...registerInvite('email', {
                    required: 'Email er påkrævet',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Ugyldig email',
                    },
                  })}
                  error={!!inviteErrors.email}
                  helperText={inviteErrors.email?.message || 'En midlertidig adgangskode genereres automatisk'}
                  sx={{ mb: 2 }}
                />

                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    disabled={createInvite.isPending}
                    startIcon={createInvite.isPending ? <CircularProgress size={20} /> : <PersonAddIcon />}
                  >
                    Opret bruger
                  </Button>
                </Box>
              </form>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>
            {inviteCredentials ? 'Luk' : 'Spring over'}
          </Button>
        </DialogActions>

        <Snackbar
          open={copySnackbar}
          autoHideDuration={2000}
          onClose={() => setCopySnackbar(false)}
          message="Kopieret til udklipsholder"
        />
      </>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <DialogTitle>
        {isEditing ? 'Rediger Organisation' : 'Ny Organisation'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {(createOrg.error || updateOrg.error) && (
            <Alert severity="error">
              {(createOrg.error as Error)?.message || (updateOrg.error as Error)?.message || 'Der opstod en fejl'}
            </Alert>
          )}

          <TextField
            label="CVR/Org.nr."
            {...register('cvr', {
              required: 'CVR/Org.nr. er påkrævet',
            })}
            error={!!errors.cvr || !!cvrError}
            helperText={errors.cvr?.message || cvrError || 'Dansk CVR, svensk org.nr., eller andet selskabsnummer'}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title="Slå CVR op (kun DK)">
                    <IconButton
                      onClick={handleCvrLookup}
                      disabled={cvrLookup.isPending}
                      edge="end"
                    >
                      {cvrLookup.isPending ? (
                        <CircularProgress size={20} />
                      ) : (
                        <SearchIcon />
                      )}
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />

          <TextField
            label="Virksomhedsnavn"
            {...register('name', { required: 'Navn er påkrævet' })}
            error={!!errors.name}
            helperText={errors.name?.message}
          />

          <TextField
            label="Adresse"
            {...register('address')}
          />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Postnummer"
              {...register('postalCode')}
              sx={{ width: '30%' }}
            />
            <TextField
              label="By"
              {...register('city')}
              sx={{ flex: 1 }}
            />
          </Box>

          <FormControl fullWidth>
            <InputLabel id="dora-env-label">DORA API Miljø</InputLabel>
            <Select
              labelId="dora-env-label"
              label="DORA API Miljø"
              defaultValue={organization?.doraApiEnvironment || 'PRODUCTION'}
              {...register('doraApiEnvironment')}
            >
              <MenuItem value="PRODUCTION">Production</MenuItem>
              <MenuItem value="STAGING">Staging</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel id="language-label">Sprog (PDF-rapporter)</InputLabel>
            <Select
              labelId="language-label"
              label="Sprog (PDF-rapporter)"
              defaultValue={organization?.language || 'da'}
              {...register('language')}
            >
              <MenuItem value="da">Dansk (DKK)</MenuItem>
              <MenuItem value="sv">Svenska (SEK)</MenuItem>
              <MenuItem value="no">Norsk (NOK)</MenuItem>
              <MenuItem value="en">English (EUR)</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label={isEditing ? 'DORA API-nøgle (lad tom for at beholde)' : 'DORA API-nøgle'}
            type={showApiKey ? 'text' : 'password'}
            {...register('doraApiKey', {
              required: isEditing ? false : 'API-nøgle er påkrævet',
            })}
            error={!!errors.doraApiKey}
            helperText={errors.doraApiKey?.message || (isEditing ? 'Efterlad tom for at beholde nuværende nøgle' : '')}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowApiKey(!showApiKey)}
                    edge="end"
                  >
                    {showApiKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
            FMS (Fleet Management System) — valgfrit
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="FMS API-nøgle"
              type="password"
              {...register('fmsApiKey')}
              sx={{ flex: 1 }}
            />
            <TextField
              label="FMS Kunde-ID"
              {...register('fmsCustomerId')}
              sx={{ width: '40%' }}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annuller</Button>
        <Button
          type="submit"
          variant="contained"
          disabled={isSubmitting || createOrg.isPending || updateOrg.isPending}
        >
          {isSubmitting || createOrg.isPending || updateOrg.isPending ? (
            <CircularProgress size={24} />
          ) : isEditing ? (
            'Gem ændringer'
          ) : (
            'Opret'
          )}
        </Button>
      </DialogActions>
    </form>
  );
}
